import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ButtonDropdown from '@ui/components/ButtonDropdown';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { Calendar2 } from 'iconsax-react';
import { useAppDispatch } from '@application/store/useStore';
import { useSelector } from 'react-redux';
import { getPartnerLogsAsync } from '@application/features/partnerLogs/partnerLogsThunk';
import { APIStatus } from '@application/enums/api-status';
const ActivityTab = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { partner } = useSelector((state) => state.partner);
    const { logs, status } = useSelector((state) => state.partnerLogs);
    const filterOptions = useMemo(() => {
        const allLabel = t('common.all', { defaultValue: 'All' });
        return [
            { id: 'all', text: allLabel },
            { id: 'today', text: t('common.today', { defaultValue: 'Today' }) },
            { id: 'yesterday', text: t('common.yesterday', { defaultValue: 'Yesterday' }) },
            { id: 'thisWeek', text: t('common.thisWeek', { defaultValue: 'This Week' }) },
            { id: 'lastWeek', text: t('common.lastWeek', { defaultValue: 'Last Week' }) },
            { id: 'thisMonth', text: t('common.thisMonth', { defaultValue: 'This Month' }) },
            {
                id: 'currentMonth',
                text: t('common.currentMonth', { defaultValue: 'Current month' })
            },
            { id: 'lastMonth', text: t('common.lastMonth', { defaultValue: 'Last month' }) },
            {
                id: 'last3Months',
                text: t('common.last3Months', { defaultValue: 'Last 3 Months' })
            },
            {
                id: 'last6Months',
                text: t('common.last6Months', { defaultValue: 'Last 6 Months' })
            },
            { id: 'thisYear', text: t('common.thisYear', { defaultValue: 'This Year' }) },
            { id: 'lastYear', text: t('common.lastYear', { defaultValue: 'Last Year' }) },
            { id: 'custom', text: t('common.dateRange', { defaultValue: 'Date range' }) }
        ];
    }, [t]);
    const [selectedFilter, setSelectedFilter] = useState(filterOptions[0].id ?? 'all');
    const [customDateRange, setCustomDateRange] = useState(null);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const activityColumns = useMemo(() => [
        {
            field: 'activity',
            headerName: t('sponsorships.activityTab.columns.activity'),
            flex: 1.2,
            minWidth: 160
        },
        {
            field: 'target',
            headerName: t('sponsorships.activityTab.columns.target'),
            flex: 1,
            minWidth: 150
        },
        {
            field: 'date',
            headerName: t('sponsorships.activityTab.columns.date'),
            flex: 0.9,
            minWidth: 140
        },
        {
            field: 'time',
            headerName: t('sponsorships.activityTab.columns.time'),
            flex: 0.7,
            minWidth: 120,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'performedBy',
            headerName: t('sponsorships.activityTab.columns.performedBy'),
            flex: 1,
            minWidth: 160
        }
    ], [t]);
    useEffect(() => {
        if (!partner?.id)
            return;
        let from;
        let to;
        let period;
        if (selectedFilter === 'today') {
            period = 'Today';
        }
        else if (selectedFilter === 'yesterday') {
            period = 'Yesterday';
        }
        else if (selectedFilter === 'thisWeek') {
            period = 'ThisWeek';
        }
        else if (selectedFilter === 'lastWeek') {
            period = 'LastWeek';
        }
        else if (selectedFilter === 'thisMonth' || selectedFilter === 'currentMonth') {
            period = 'ThisMonth';
        }
        else if (selectedFilter === 'lastMonth') {
            period = 'LastMonth';
        }
        else if (selectedFilter === 'last3Months') {
            period = 'Last3Months';
        }
        else if (selectedFilter === 'last6Months') {
            period = 'Last6Months';
        }
        else if (selectedFilter === 'thisYear') {
            period = 'ThisYear';
        }
        else if (selectedFilter === 'lastYear') {
            period = 'LastYear';
        }
        else if (selectedFilter === 'custom' &&
            customDateRange?.startDate &&
            customDateRange?.endDate) {
            from = customDateRange.startDate;
            to = customDateRange.endDate;
        }
        void dispatch(getPartnerLogsAsync({
            partnerId: partner.id,
            period,
            from,
            to,
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize
        }));
    }, [
        dispatch,
        partner?.id,
        paginationModel.page,
        paginationModel.pageSize,
        selectedFilter,
        customDateRange?.startDate,
        customDateRange?.endDate
    ]);
    const activityRows = useMemo(() => {
        // Normalize backend date strings which may have >3 fractional seconds and no timezone
        const normalizeIsoDate = (value) => {
            if (!value || typeof value !== 'string')
                return '';
            let working = value.trim();
            // Trim fractional seconds to max 3 digits
            // e.g., 2025-12-22T18:48:41.1687799 → 2025-12-22T18:48:41.168
            working = working.replace(/(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})\.(\d+)(.*)/, (_, base, fraction, tail) => {
                const ms = fraction.slice(0, 3).padEnd(3, '0');
                return `${base}.${ms}${tail}`;
            });
            // Append Z if no explicit timezone offset/Z provided
            if (!/Z|[+-]\d{2}:\d{2}$/.test(working)) {
                working = `${working}Z`;
            }
            return working;
        };
        const rows = (logs?.items || []).map((l) => {
            const rawDate = l?.date || l?.createdDate;
            const normalizedDateStr = normalizeIsoDate(rawDate);
            const dateObj = normalizedDateStr ? new Date(normalizedDateStr) : undefined;
            const validDate = dateObj && !isNaN(dateObj.getTime()) ? dateObj : undefined;
            const monthKey = validDate ? validDate.toISOString().slice(0, 7) : 'unknown';
            const dateStr = validDate
                ? validDate.toLocaleDateString(undefined, {
                    month: 'short',
                    day: '2-digit',
                    year: 'numeric'
                })
                : '';
            const timeStr = l.time ||
                (validDate ? validDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
            return {
                id: l.id,
                monthKey,
                activity: l.description || l.action || l.activityType || '',
                target: l.target || '',
                date: dateStr,
                time: timeStr,
                performedBy: l.performedBy || l?.createdByName || ''
            };
        });
        return rows;
    }, [logs]);
    return (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '12px', children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', children: [_jsx(Typography, { variant: 'h6', children: t('sponsorships.activity') }), _jsx(ButtonDropdown, { label: t('sponsorships.activityTab.monthFilter'), keepOriginalOrder: true, options: filterOptions, variant: 'outlined', size: 'large', color: 'primary', startIcon: _jsx(Calendar2, { variant: 'Outline' }), selectedItem: selectedFilter, onSelectionChange: (value) => {
                            setSelectedFilter(value);
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                            if (value !== 'custom') {
                                setCustomDateRange(null);
                            }
                        }, enableDateRange: true, customDateRange: customDateRange, onDateRangeChange: (range) => {
                            setCustomDateRange(range);
                            setSelectedFilter('custom');
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        } })] }), _jsx(PrimaryGrid, { rows: activityRows, columns: activityColumns, showFilters: false, showHeaderControls: false, maxHeight: 'calc(100dvh - 260px)', hideFooterSelectedRowCount: true, paginationMode: 'server', rowCount: logs?.totalCount ?? 0, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, loading: status === APIStatus.PENDING, pageSizeOptions: [5, 10, 20, 50, 100] })] }));
};
export default ActivityTab;
